import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../nav_bar/Navbar";
import "./clientEvent.css";
import { bookEvent } from "./clientEventService";

/**
 * ===============================
 * SAFE DEBUG LOGGER
 * ===============================
 */

const isDev = process.env.NODE_ENV === "development";

const debugLog = (...args) => {
  if (isDev) console.log("[BookingPage]", ...args);
};

const debugWarn = (...args) => {
  if (isDev) console.warn("[BookingPage]", ...args);
};

const debugError = (...args) => {
  if (isDev) console.error("[BookingPage]", ...args);
};


const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const BookingPage = () => {

  const navigate = useNavigate();
  const location = useLocation();

  const event = location.state?.event;

  /**
   * ===============================
   * USER STATE
   * ===============================
   */

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [alert, setAlert] = useState({
  show: false,
  type: "info", // success | error | warning
  message: ""
});


  /**
   * ===============================
   * LOAD LOGGED-IN USER
   * ===============================
   */

  useEffect(() => {

    debugLog("Checking logged-in user");

    try {

      const storedUser = localStorage.getItem("user");
      const token = localStorage.getItem("token");

      if (storedUser && token) {

        const parsedUser = JSON.parse(storedUser);

        debugLog("User found:", parsedUser);

        setName(parsedUser.name || "");
        setEmail(parsedUser.email || "");
        setIsLoggedIn(true);

      } else {

        debugLog("No logged-in user");

        setIsLoggedIn(false);

      }

    } catch (error) {

      debugError("Failed to parse user:", error);
      setIsLoggedIn(false);

    } finally {

      setLoadingUser(false);

    }

  }, []);


  /**
   * ===============================
   * DEBUG LOCATION STATE
   * ===============================
   */

  useEffect(() => {
    debugLog("Location object:", location);
    debugLog("Location state:", location.state);
    debugLog("Event received:", event);
  }, [location, event]);


  /**
   * ===============================
   * INVALID EVENT CHECK
   * ===============================
   */

  if (!event) {

    debugWarn("No event received. Redirecting user.");

    return (
      <div className="client-container">

        <h3>Invalid booking request</h3>

        <button onClick={() => navigate("/events")}>
          Back to Events
        </button>

      </div>
    );

  }


  const showAlert = (type, message) => {

  setAlert({
    show: true,
    type,
    message
  });

  // auto hide after 4 seconds
  setTimeout(() => {
    setAlert(prev => ({ ...prev, show: false }));
  }, 4000);

};


  /**
   * ===============================
   * DATE FORMAT
   * ===============================
   */

  const formatDate = (value) => {

    try {

      const date = new Date(value);

      if (isNaN(date.getTime())) {
        debugWarn("Invalid date:", value);
        return value;
      }

      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });

    } catch (e) {

      debugError("formatDate error:", e);
      return value;

    }

  };


  /**
   * ===============================
   * TIME FORMAT
   * ===============================
   */

  const formatTime = (value) => {

    try {

      const date = new Date(value);

      if (isNaN(date.getTime())) {
        debugWarn("Invalid time:", value);
        return value;
      }

      return date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true
      });

    } catch (e) {

      debugError("formatTime error:", e);
      return value;

    }

  };


  /**
   * ===============================
   * BOOK EVENT
   * ===============================
   */

  const handleBooking = async () => {

    debugLog("Booking started");

    if (!name.trim()) {

      debugWarn("Name missing");
      showAlert("warning", "Please enter your name");
      return;

    }

    if (!email.trim()) {

      debugWarn("Email missing");
      showAlert("warning", "Please enter your email");
      return;

    }

    if (!consent) {

      debugWarn("Consent not provided");
      showAlert("warning", "Please provide consent");
      return;

    }

    try {

      setLoading(true);

      const payload = {
        eventId: event.id,
        name: name.trim(),
        email: email.trim(),
        timeZone: userTimeZone
      };

      debugLog("Booking payload:", payload);

      const response = await bookEvent(payload);

      debugLog("Booking response:", response);

      if (response.status === 200) {

        debugLog("Booking success");
        setSuccess(true);

      } else {

        debugWarn("Booking failed:", response.message);
        showAlert("error", response.message || "Booking failed");

      }

    } catch (error) {

      debugError("Booking error:", error);

      showAlert(
  "error",
  error?.response?.data?.message || "Booking failed"
);

    } finally {

      setLoading(false);

    }

  };


  /**
   * ===============================
   * SUCCESS UI
   * ===============================
   */

  if (success) {

    return (

      <div className="client-container">

        <div className="booking-card">

          <h3>Booking Confirmed 🎉</h3>

          <p>Your seat has been booked successfully.</p>

          <button
            className="book-btn"
            onClick={() => navigate("/events")}
          >
            Back to Events
          </button>

        </div>

      </div>

    );

  }


  /**
   * ===============================
   * WAIT FOR USER LOAD
   * ===============================
   */

  if (loadingUser) {

    return (

      <div className="client-container">

        <div className="booking-card">
          Loading...
        </div>

      </div>

    );

  }


  /**
   * ===============================
   * MAIN UI
   * ===============================
   */

return (

    <> <Navbar />

  {alert.show && (
  <div className="alert-overlay">

    <div className={`alert-modal ${alert.type}`}>

      <div className="alert-message">
        {alert.message}
      </div>

      <button
        className="alert-btn"
        onClick={() =>
          setAlert(prev => ({ ...prev, show: false }))
        }
      >
        OK
      </button>

    </div>

  </div>
)}

  <div className="client-container">

    {/* BACK */}
    <button
      className="back-btn"
      onClick={() => navigate("/events")}
    >
      ← Back to events
    </button>


    <div className="booking-layout">

      {/* EVENT SUMMARY */}
      <div className="event-summary-card">

        <h2 className="event-summary-title">
          {event.title}
        </h2>

        <p className="event-summary-desc">
          {event.description}
        </p>

       <div className="event-info-modern">

  <div className="info-row-modern">

    <div className="info-left">
      <span className="info-icon">📅</span>
      <span className="info-label">Date</span>
    </div>

    <div className="info-value large">
      {formatDate(event.eventStartTime)}
    </div>

  </div>


  <div className="info-row-modern">

    <div className="info-left">
      <span className="info-icon">⏰</span>
      <span className="info-label">Time</span>
    </div>

    <div className="info-value large">
      {formatTime(event.eventStartTime)}
    </div>

  </div>


  <div className="info-row-modern">

    <div className="info-left">
      <span className="info-icon">⏳</span>
      <span className="info-label">Duration</span>
    </div>

    <div className="info-value">
      {event.eventDuration} minutes
    </div>

  </div>

</div>

      </div>



      {/* BOOKING FORM */}
      <div className="booking-card">

        <h3 className="booking-title">
          Confirm your booking
        </h3>


        {!isLoggedIn && (

          <>
            <div className="input-group">
<input
  type="text"
  placeholder="Enter your name"
  autoComplete="name"
  value={name || ""}
  onChange={(e) => setName(e.target.value)}
/>

            </div>


            <div className="input-group">

            

  <input
  type="email"
  placeholder="Enter your email"
  autoComplete="email"
  value={email || ""}
  onChange={(e) => setEmail(e.target.value)}
/>

            </div>

          </>

        )}


        {isLoggedIn && (

          <div className="logged-user-info">

            <div>
              <span>Name</span>
              <strong>{name}</strong>
            </div>

            <div>
              <span>Email</span>
              <strong>{email}</strong>
            </div>

          </div>

        )}



        {/* CONSENT */}

        <label className="consent">

          <input
            type="checkbox"
            checked={consent}
            onChange={(e) =>
              setConsent(e.target.checked)
            }
          />

          <span>
            I agree to receive emails from SpeakBoldWithKK
          </span>

        </label>



       <button
  className="book-btn primary-btn"
  onClick={handleBooking}
  disabled={loading || !consent}
>
  {loading ? "Confirming..." : "Confirm Booking"}
</button>

      </div>

    </div>

  </div>

  </>

);

};

export default BookingPage;